const MAX_SOURCE_BYTES = 10 * 1024 * 1024;
const MAX_OUTPUT_BYTES = 600 * 1024;
const MAX_EDGE = 800;

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("No se pudo leer la imagen seleccionada."));
    image.src = url;
  });
}

function canvasBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("El navegador no pudo procesar la imagen."));
        return;
      }
      resolve(blob);
    }, "image/webp", quality);
  });
}

/** Resize and encode an employee photo before it crosses the network. */
export async function compressProviderPhoto(source: File): Promise<File> {
  if (!source.type.startsWith("image/")) throw new Error("Seleccioná una imagen válida.");
  if (source.size > MAX_SOURCE_BYTES) throw new Error("La imagen original no puede superar los 10 MB.");

  const objectUrl = URL.createObjectURL(source);
  try {
    const image = await loadImage(objectUrl);
    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;
    if (!sourceWidth || !sourceHeight) throw new Error("La imagen no tiene unas dimensiones válidas.");

    const scale = Math.min(1, MAX_EDGE / Math.max(sourceWidth, sourceHeight));
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("El navegador no admite la edición de imágenes.");
    context.drawImage(image, 0, 0, width, height);

    let quality = 0.82;
    let encoded = await canvasBlob(canvas, quality);
    while (encoded.size > MAX_OUTPUT_BYTES && quality > 0.5) {
      quality = Math.max(0.5, quality - 0.08);
      encoded = await canvasBlob(canvas, quality);
    }
    if (encoded.type !== "image/webp") throw new Error("El navegador no pudo convertir la imagen a WebP.");

    const basename = source.name.replace(/\.[^/.]+$/, "") || "empleado";
    return new File([encoded], `${basename}.webp`, { type: "image/webp", lastModified: Date.now() });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function providerPhotoUrl(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value)) return value;
  const apiBase = (import.meta.env.VITE_API_URL ?? "http://localhost:8787").replace(/\/$/, "");
  return `${apiBase}${value}`;
}
